<template>
  <div class="order-list" :class="{ 'order-list--indonesia': $i18n.locale === 'id-ID' }">
    <van-nav-bar :fixed="true" z-index="9" :title="title" :border="false" left-arrow @click-left="onClickLeft" />
    <div class="order-list__filters">
      <div v-for="filter in filters" :key="filter.value" class="order-list__filter" :class="{ 'is-selected': groupStatus === filter.value, ['is-filter-' + filter.value]: true }" @click="selectFilter(filter.value)">
        {{ $t(filter.label) }}
      </div>
    </div>
    <div class="order-list__list">
      <van-pull-refresh
        v-model="refreshing"
        :pulling-text="$t('loading')"
        :loosing-text="$t('loading')"
        :loading-text="$t('loading')"
        success-text=""
        :success-duration="0"
        @refresh="onRefresh"
      >
      <van-list v-model="loading" :finished="finished" :finished-text="finishedText" :loading-text="$t('loading')" @load="onLoad">
        <OrderListItem v-for="item in orderList" :order="item" :key="item.id" @orderClick="orderClick">
        </OrderListItem>
      </van-list>
      </van-pull-refresh>
    </div>
    <div class="order-list__empty" v-if="isEmpty">
      <div class="order-list__empty-illustration" aria-hidden="true">
        <div class="order-list__empty-icon">
          <span class="order-list__assignment-icon"></span>
        </div>
      </div>
      <h2>{{ $t('orderList.empty') }}</h2>
      <p>{{ $t('orderList.emptyDesc') }}</p>
      <button type="button" class="order-list__empty-action" @click="goShop">
        {{ $t('orderList.shopNow') }}
      </button>
    </div>
    <ExpressProgress ref="deliveryProgress" :express="express"></ExpressProgress>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { List, PullRefresh, Dialog } from 'vant';
import OrderListItem from './components/OrderListItem.vue';
import { queryOrderList, queryExpressByOrderId } from '@/api/order';
import { ORDER_STATUS } from '@/common/constants';
import ExpressProgress from '@/components/ExpressProgress.vue';
import routerBack from '@/mixins/routerBack';
const ORDER_LIST_SCROLL_TOP_KEY = 'orderListScrollTop';
const ORDER_LIST_RESTORE_SCROLL_KEY = 'orderListRestoreScroll';
const DEFAULT_GROUP_STATUS = 'active';
export default {
  name: 'OrderList',
  mixins: [routerBack],
  components: {
    OrderListItem,
    [List.name]: List,
    [PullRefresh.name]: PullRefresh,
    ExpressProgress
  },
  computed: {
    ...mapState({
      accessToken: state => state.user.session?.accessToken || ''
    }),
    title() {
      return this.$t('orderList.myOrders');
    },
    finishedText() {
      return this.orderList.length > 5 ? this.$t('noMore') : '';
    },
    isEmpty() {
      return this.orderList.length === 0 && this.finished;
    },
    filters() {
      return [
        { value: 'all', label: 'orderList.filters.all' },
        { value: 'active', label: 'orderList.filters.active' },
        { value: 'breaching', label: 'orderList.filters.breaching' },
        { value: 'completed', label: 'orderList.filters.completed' },
        { value: 'closed', label: 'orderList.filters.closed' }
      ];
    }
  },
  data() {
    return {
      loading: false,
      finished: false,
      orderList: [],
      pageNum: 1,
      pageSize: 20,
      express: null,
      refreshing: false,
      groupStatus: DEFAULT_GROUP_STATUS,
      requestSeq: 0,
      replacing: false,
      cachedAccessToken: '',
      handledAccountRefreshToken: '',
    };
  },
  activated() {
    this.syncAuthState();
    if (this.refreshFromAccountIfNeeded()) return;
    this.reloadFirstPageIfActive();
    this.restoreScrollPosition();
  },
  watch: {
    accessToken(token) {
      this.syncAuthState();
      if (token) {
        this.$nextTick(() => this.reloadFirstPageIfActive());
      }
    }
  },
  methods: {
    ...mapActions('orders', ['cancelOrder']),
    syncAuthState() {
      if (this.cachedAccessToken === this.accessToken) return;
      this.cachedAccessToken = this.accessToken;
      this.resetListState();
    },
    resetListState() {
      this.requestSeq++;
      this.loading = false;
      this.finished = false;
      this.orderList = [];
      this.pageNum = 1;
      this.refreshing = false;
      this.replacing = false;
      this.express = null;
      this.clearSavedScrollPosition();
    },
    reloadFirstPageIfActive() {
      if (!this.accessToken || this.$route.name !== 'OrderList' || this.loading || this.orderList.length) return;
      this.onLoad();
    },
    refreshFromAccountIfNeeded() {
      const { from, refreshToken } = this.$route.query || {};
      const token = String(refreshToken || '');
      if (from !== 'account' || !token || token === this.handledAccountRefreshToken) return false;
      this.handledAccountRefreshToken = token;
      this.clearSavedScrollPosition();
      this.requestSeq++;
      this.loading = false;
      this.finished = false;
      this.orderList = [];
      this.pageNum = 1;
      this.refreshing = false;
      this.replacing = true;
      this.express = null;
      this.groupStatus = DEFAULT_GROUP_STATUS;
      window.scrollTo(0, 0);
      if (this.accessToken) {
        this.$nextTick(() => this.onLoad());
      } else {
        this.finished = true;
      }
      return true;
    },
    onLoad() {
      this.syncAuthState();
      if (!this.accessToken) {
        this.resetListState();
        this.finished = true;
        return;
      }
      // 异步更新数据
      this.loading = true;
      const requestSeq = ++this.requestSeq;
      const pageNum = this.pageNum;
      return queryOrderList({
        pageNum,
        pageSize: this.pageSize,
        groupStatus: this.groupStatus
      }).then(res => {
        if (requestSeq !== this.requestSeq) return;
        // 加载状态结束
        this.loading = false;
        this.refreshing = false;
        if (res?.code === 'Success' && res.data.records?.length > 0) {
          this.pageNum = pageNum + 1;
          this.orderList = this.replacing ? res.data.records : this.orderList.concat(res.data.records);
          this.replacing = false;
          if (res.data.records.length < this.pageSize) {
            this.finished = true;
          }
        } else {
          if (this.replacing) this.orderList = [];
          this.replacing = false;
          this.finished = true;
        }
        this.$nextTick(() => this.restoreScrollPosition());
      }, () => {
        if (requestSeq !== this.requestSeq) return;
        // 加载状态结束
        this.loading = false;
        this.refreshing = false;
        this.replacing = false;
      })
    },
    onRefresh() {
      this.clearSavedScrollPosition();
      this.requestSeq++;
      this.pageNum = 1;
      this.finished = false;
      this.replacing = true;
      this.refreshing = true;
      this.onLoad();
    },
    selectFilter(value) {
      if (value === this.groupStatus) return;
      this.clearSavedScrollPosition();
      this.requestSeq++;
      this.groupStatus = value;
      this.pageNum = 1;
      this.finished = false;
      this.orderList = [];
      this.replacing = true;
      this.loading = false;
      this.$nextTick(() => this.onLoad());
    },
    onClickLeft() {
      this.goBack()
    },
    saveScrollPosition() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      sessionStorage.setItem(ORDER_LIST_SCROLL_TOP_KEY, String(scrollTop));
      sessionStorage.setItem(ORDER_LIST_RESTORE_SCROLL_KEY, 'true');
    },
    clearSavedScrollPosition() {
      sessionStorage.removeItem(ORDER_LIST_SCROLL_TOP_KEY);
      sessionStorage.removeItem(ORDER_LIST_RESTORE_SCROLL_KEY);
    },
    restoreScrollPosition(attempt = 0) {
      if (sessionStorage.getItem(ORDER_LIST_RESTORE_SCROLL_KEY) !== 'true') return;
      const savedScrollTop = Number(sessionStorage.getItem(ORDER_LIST_SCROLL_TOP_KEY) || 0);
      if (!Number.isFinite(savedScrollTop) || savedScrollTop <= 0) {
        this.clearSavedScrollPosition();
        return;
      }
      this.$nextTick(() => {
        window.scrollTo(0, savedScrollTop);
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const restored = Math.abs(currentScrollTop - savedScrollTop) <= 4;
        if (restored || attempt >= 6) {
          this.clearSavedScrollPosition();
          return;
        }
        window.setTimeout(() => this.restoreScrollPosition(attempt + 1), 50);
      });
    },
    orderClick({ type, item, reason, ...refundData }) {
      switch (type) {
        case 'cancelOrder':
          this.doCancelOrder({ orderId: item.orderId, reason, ...refundData });
          break;
        case 'rentalPlan':
          this.goRentalPlan(item);
          break;
        case 'deliveryProgress':
          this.showDeliveryProgress(item);
          break;
        case 'orderDetail':
        case 'payNow':
          this.goDetail(item);
          break;
        case 'terminalDetail':
          this.goTerminate(item);
          break;
      }
    },
    goRentalPlan(item) {
      this.saveScrollPosition();
      this.$router.push({
        name: 'RentalplanDetail',
        query: {
          orderId: item.orderId
        }
      })
    },
    goDetail(order) {
      this.saveScrollPosition();
      this.$router.push({
        name: 'OrderDetail',
        query: {
          orderId: order.orderId
        }
      })
    },
    goTerminate(order) {
      this.saveScrollPosition();
      this.$router.push({
        name: 'Terminate',
        query: {
          orderId: order.orderId
        }
      })
    },
    goShop() {
      this.$router.push({ name: 'GoodsList' });
    },
    showDeliveryProgress({ orderId }) {
      if (this.express && this.express.orderId === orderId) {
        this.$refs.deliveryProgress.open();
        return;
      }
      queryExpressByOrderId({
        orderId
      }).then(res => {
        if (res?.code === 'Success') {
          this.express = {
            orderId,
            ...res.data
          };
          this.$refs.deliveryProgress.open();
        }else{
          this.$toast(res.message || this.$t('systemError'));
        }
      }).catch(() => {
        this.$toast(this.$t('systemError'))
      })
    },
    doCancelOrder({ orderId, reason, ...refundData }) {
      const doRequest = async () => {
        let res = await this.cancelOrder({ orderId, reason, ...refundData });
        if (res && res.code === 'Success') {
          this.clearSavedScrollPosition();
          this.orderList = [];
          this.pageNum = 1;
          this.finished = false;
          this.loading = true;
          this.onLoad();
        }
      }
      if (reason !== undefined) {
        doRequest()
      } else {
        Dialog.confirm({
          title: this.$t('orderList.cancelTitle'),
          message: this.$t('orderList.cancelContent'),
          confirmButtonText: this.$t('confirm'),
          cancelButtonText: this.$t('cancel'),
        }).then(doRequest)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.order-list {
  min-height: 100vh;
  background: #F4F8FF;
  padding-top: 96px;
  box-sizing: border-box;
  font-family: 'RentalInter', Helvetica, Arial, sans-serif;

  &--indonesia {
    font-family: 'RentalPlusJakarta', Helvetica, Arial, sans-serif;
  }

  &__filters {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding: 20px 30px 8px;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }

  &__filter {
    flex: 0 0 auto;
    padding: 10px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    color: #6b7280;
    font-size: 22px;
    line-height: 28px;
    &.is-selected { font-weight: 600; }
    &.is-selected.is-filter-all { color: #fff; background: #2547f1; border-color: #2547f1; }
    &.is-selected.is-filter-active { color: #2547f1; background: #edf1ff; border-color: #2547f1; }
    &.is-selected.is-filter-breaching { color: #5128c7; background: #f0edff; border-color: #5128c7; }
    &.is-selected.is-filter-completed { color: #68aa36; background: #eff8e9; border-color: #68aa36; }
    &.is-selected.is-filter-closed { color: #68686c; background: #f1f1f1; border-color: #68686c; }
  }

  &__list {
    padding: 16px 30px 30px;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 174px 40px 0;
    text-align: center;

    h2 {
      margin: 48px 0 0;
      color: #1f2a5a;
      font-size: 32px;
      font-weight: 500;
      line-height: 44px;
    }

    p {
      max-width: 520px;
      margin: 16px 0 0;
      color: #64748b;
      font-size: 26px;
      font-weight: 400;
      line-height: 38px;
    }
  }

  &__empty-illustration {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: #eaf2ff;
  }

  &__empty-icon {
    position: relative;
    width: 116px;
    height: 136px;
    border: 8px solid #2563eb;
    border-radius: 20px;
    background: #ffffff;
    box-sizing: border-box;

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 28px;
      width: 48px;
      height: 8px;
      border-radius: 999px;
      background: #2563eb;
    }

    &::before {
      top: 38px;
    }

    &::after {
      top: 70px;
    }
  }

  &__assignment-icon {
    position: absolute;
    left: 28px;
    bottom: 26px;
    width: 36px;
    height: 8px;
    border-radius: 999px;
    background: #2563eb;
  }

  &__empty-action {
    width: 360px;
    height: 88px;
    margin-top: 48px;
    border: 0;
    border-radius: 16px;
    background: #2563eb;
    color: #ffffff;
    font-size: 30px;
    font-weight: 500;
    line-height: 88px;
    text-align: center;
    cursor: pointer;

    &:active {
      background: #1d4ed8;
    }
  }

  /* Android My Orders uses an 8dp grid. Values are doubled for this 750px H5 design scale. */
  &__filters {
    gap: 16px;
    padding: 16px;
  }

  &__filter {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 64px;
    padding: 8px 24px;
    border-color: #e5eaf3;
    border-radius: 16px;
    color: #64748b;
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;

    &.is-selected {
      font-weight: 600;
    }
    &.is-selected.is-filter-all { color: #fff; background: #2563eb; border-color: #2563eb; }
    &.is-selected.is-filter-active { color: #2563eb; background: #eff5ff; border-color: #2563eb; }
    &.is-selected.is-filter-breaching { color: #7c3aed; background: #f3efff; border-color: #7c3aed; }
    &.is-selected.is-filter-completed { color: #22c55e; background: #eafbf2; border-color: #22c55e; }
    &.is-selected.is-filter-closed { color: #64748b; background: #f3f4f6; border-color: #64748b; }
  }

  &__list {
    padding: 0 16px calc(72px + env(safe-area-inset-bottom));
  }
}

::v-deep .van-list {
  box-sizing: border-box;
  min-height: calc(100vh - 192px);
  padding-bottom: 48px;
}

@font-face {
  font-family: 'RentalInter';
  src: url('~@/assets/fonts/inter.ttf') format('truetype');
  font-style: normal;
  font-weight: 100 900;
}

@font-face {
  font-family: 'RentalPlusJakarta';
  src: url('~@/assets/fonts/plus_jakarta_sans.ttf') format('truetype');
  font-style: normal;
  font-weight: 100 900;
}

.order-list button {
  font-family: inherit;
}

::v-deep .van-nav-bar {
  height: 96px;
}

::v-deep .van-nav-bar__content {
  height: 96px;
}

::v-deep .van-nav-bar__title {
  max-width: calc(100% - 176px);
  color: #1f2a5a;
  font-size: 36px;
  font-weight: 600;
  line-height: 52px;
}

::v-deep .van-nav-bar__left {
  display: flex;
  align-items: center;
  width: 88px;
  height: 96px;
  padding: 0;
}

::v-deep .van-nav-bar__arrow {
  margin-left: 16px;
  color: #1f2a5a;
  font-size: 56px;
}

::v-deep .van-pull-refresh__text {
  font-size: 24px;
  line-height: 34px;
  font-weight: 400;
  color: #64748B;
}
</style>
